import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2, Download, Plus, Upload, UploadCloud, X } from 'lucide-react'
import { importTasks, type Client, type Employee } from '../../lib/api'
// Reuse the proven, generic parsing primitives from the client intake pipeline.
import { parseFile, parseDelimited } from '../client/intake/parse'
import type { ParsedTable } from '../client/intake/types'
import { downloadTaskTemplate } from '../../lib/download-template'
import { useDisciplines } from '../../hooks/useDisciplines'
import { FIELDS, autoMap, mapRows, type FieldKey } from './mapTasks'

const ACCEPT = '.csv,.tsv,.txt,.xlsx,.xlsm,.xlsb,.xls,.ods'
const MAX_IMPORT = 500
const PREVIEW = 8

const SAMPLE = `title\tdetails\tstatus\tpriority\tdiscipline\tdue\tclient\tassignee
Kickoff call\tAgenda and goals for the first call\ttodo\thigh\tops\t2026-08-01\tHarbor View Dentistry\tSarah Chen
Draft ad creative\tTwo concepts for the launch\tin_progress\tmed\tmarketing\t2026-08-05\tLoft & Co Salon\tMarcus Webb
Publish GBP posts\t\ttodo\tlow\tmarketing\t\tBayside Auto Care\t`

/** How many per-row problems to spell out before summarising the rest. */
const MAX_ISSUES_SHOWN = 50

/**
 * Turn the API's `fields` map into the same "Row N — reason" lines the client-
 * side checks produce.
 *
 * The server keys errors by position in the submitted array (`tasks.7.title`),
 * which is meaningless to someone looking at a spreadsheet. Mapping the index
 * back through the submitted rows recovers the real sheet row. Without this the
 * whole response collapses to the word "Validation failed".
 */
function serverRowIssues(err: unknown, submitted: { sourceRow: number }[]): string[] {
  const fields = (err as { body?: { error?: { fields?: Record<string, string> } } })?.body?.error?.fields
  if (!fields) return []
  const lines: string[] = []
  for (const [key, message] of Object.entries(fields)) {
    const m = /^tasks\.(\d+)\.?(.*)$/.exec(key)
    if (!m) {
      lines.push(message)
      continue
    }
    const row = submitted[Number(m[1])]
    lines.push(row ? `Row ${row.sourceRow} — ${message}` : message)
  }
  return lines
}

interface TaskImportProps {
  onClose: () => void
  clients: Client[]
  employees: Employee[]
}

export function TaskImport({ onClose, clients, employees }: TaskImportProps) {
  const qc = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const [table, setTable] = useState<ParsedTable | null>(null)
  const [colMap, setColMap] = useState<Record<number, FieldKey | null>>({})
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [parsing, setParsing] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [skipInvalid, setSkipInvalid] = useState(true)
  const disciplines = useDisciplines()

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  const clientByName = useMemo(
    () => new Map(clients.map((c) => [c.name.toLowerCase(), c.id])),
    [clients],
  )
  const mapped = useMemo(
    () => (table ? mapRows(table, colMap, clientByName, disciplines, employees) : []),
    [table, colMap, clientByName, disciplines, employees],
  )
  const valid = mapped.filter((r) => r.errors.length === 0)
  const invalid = mapped.filter((r) => r.errors.length > 0)
  const importable = skipInvalid ? valid : mapped
  const hasTitleCol = Object.values(colMap).includes('title')
  const overLimit = importable.length > MAX_IMPORT

  const mutation = useMutation({
    mutationFn: () =>
      importTasks(
        importable.map((r) => ({
          ...r.draft,
          ...(r.assigneeIds.length ? { assigneeIds: r.assigneeIds } : {}),
        })),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] })
      qc.invalidateQueries({ queryKey: ['assignments'] })
      onClose()
    },
  })

  // Server rejections are positional; translate them back to sheet rows.
  const serverIssues = mutation.isError ? serverRowIssues(mutation.error, importable) : []

  const adoptTable = (parsed: ParsedTable) => {
    setTable(parsed)
    setColMap(autoMap(parsed.headers))
  }

  const handleFile = async (file: File) => {
    setError(null)
    setParsing(true)
    try {
      const parsed = await parseFile(file, { sheetHint: /task/i })
      if (!parsed || parsed.rows.length === 0) setError('No data rows found — need a header row plus records.')
      else adoptTable(parsed)
    } catch (err) {
      setError(err instanceof Error ? `Could not read the file: ${err.message}` : 'Could not read the file.')
    } finally {
      setParsing(false)
    }
  }

  const parsePasted = () => {
    setError(null)
    const parsed = parseDelimited(text)
    if (!parsed || parsed.rows.length === 0) setError('Paste a header row and at least one task row.')
    else adoptTable(parsed)
  }

  const setColumn = (colIdx: number, key: FieldKey | null) => {
    setColMap((prev) => {
      const next = { ...prev, [colIdx]: key }
      // A destination field maps to at most one column — clear any prior owner.
      if (key) {
        for (const k of Object.keys(next)) {
          const idx = Number(k)
          if (idx !== colIdx && next[idx] === key) next[idx] = null
        }
      }
      return next
    })
  }

  const canImport = hasTitleCol && importable.length > 0 && importable.length <= MAX_IMPORT && (skipInvalid || invalid.length === 0)

  return (
    <div className="quick-add-backdrop" onClick={onClose}>
      <div className="intake-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Import tasks">
        <div className="intake-header">
          <span className="intake-header-icon"><Upload size={18} /></span>
          <div className="intake-header-text">
            <h3>Import tasks</h3>
            <p>Bulk-load tasks from a spreadsheet or pasted rows.</p>
          </div>
          <button onClick={onClose} className="btn-icon intake-close" aria-label="Close"><X size={18} /></button>
        </div>

        <div className="intake-content">
          {!table ? (
            <>
              <button
                type="button"
                className={`intake-dropzone${dragging ? ' intake-dropzone--active' : ''}`}
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files?.[0]; if (f) handleFile(f) }}
              >
                <span className="intake-dropzone-icon"><UploadCloud size={26} /></span>
                <span className="intake-dropzone-title">{parsing ? 'Reading…' : 'Drop a spreadsheet or click to browse'}</span>
                <span className="intake-dropzone-sub">CSV, TSV, or Excel · up to 500 tasks</span>
                <input ref={inputRef} type="file" accept={ACCEPT} hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f) }} />
              </button>

              <p className="intake-eyebrow" style={{ marginTop: 'var(--space-2)' }}>or paste rows</p>
              <textarea
                className="intake-input intake-textarea intake-paste-area"
                rows={5}
                placeholder={'title, status, priority, discipline, due, client\nKickoff call, todo, high, ops, 2026-08-01, Harbor View Dentistry'}
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
              {error && <p className="intake-errors intake-errors--block">{error}</p>}
              <div className="intake-footer">
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setText(SAMPLE)}>Use sample</button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => downloadTaskTemplate(disciplines)}><Download size={14} /> Download template</button>
                <button type="button" className="btn btn-primary btn-sm" onClick={parsePasted} disabled={!text.trim()}>Parse rows</button>
              </div>
            </>
          ) : (
            <div className="intake-import-review">
              <div className="intake-import-summary">
                <div className="intake-import-source">
                  <span className="intake-import-source-name">{table.source}</span>
                  <span className="intake-import-source-meta">{table.rows.length} rows · {table.headers.length} columns</span>
                </div>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setTable(null); setError(null) }}>Start over</button>
              </div>

              {!hasTitleCol ? (
                <div className="intake-banner intake-banner--warn"><AlertTriangle size={15} /><span>No column mapped to <strong>Title</strong>. Add a "title" header column.</span></div>
              ) : (
                <div className="intake-import-stats">
                  <span className="intake-stat intake-stat--ok"><CheckCircle2 size={14} /> {valid.length} ready</span>
                  {invalid.length > 0 && <span className="intake-stat intake-stat--bad"><AlertTriangle size={14} /> {invalid.length} with issues</span>}
                </div>
              )}

              {/* The only place the accepted vocabulary is written down — a
                  mis-typed status was previously only discoverable by failing. */}
              <details className="intake-col-help">
                <summary>What goes in each column</summary>
                <ul>
                  <li><strong>Title</strong> — required. Everything else is optional.</li>
                  <li><strong>Details</strong> — free text; becomes the task's notes.</li>
                  <li><strong>Status</strong> — todo · in_progress · review · done · blocked</li>
                  <li><strong>Priority</strong> — low · med · high · urgent</li>
                  <li><strong>Discipline</strong> — {disciplines.join(' · ')}</li>
                  <li><strong>Due</strong> — a date, e.g. 2026-08-01</li>
                  <li><strong>Client</strong> / <strong>Assignee</strong> — must match an existing name; separate several assignees with commas.</li>
                </ul>
                <p>Columns set to “Ignore” are not imported.</p>
              </details>

              {overLimit && (
                <div className="intake-banner intake-banner--warn">
                  <AlertTriangle size={15} />
                  <span>
                    {importable.length} rows exceeds the {MAX_IMPORT}-row import limit. Split the file
                    and import it in batches.
                  </span>
                </div>
              )}

              <div className="intake-table-scroll">
                <table className="intake-table">
                  <thead>
                    <tr>
                      <th className="intake-table-rownum" />
                      {table.headers.map((h, i) => (
                        <th key={i}>
                          <div className="intake-col-header" title={h}>{h || '(blank)'}</div>
                          <select
                            className="intake-col-select"
                            value={colMap[i] ?? ''}
                            onChange={(e) => setColumn(i, (e.target.value || null) as FieldKey | null)}
                          >
                            <option value="">— Ignore —</option>
                            {FIELDS.map((f) => (
                              <option key={f.key} value={f.key}>{f.label}{f.required ? ' *' : ''}</option>
                            ))}
                          </select>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {mapped.slice(0, PREVIEW).map((row, ri) => (
                      <tr key={ri} className={row.errors.length ? 'intake-table-row--bad' : ''}>
                        {/* The row number stays put when a row is bad — that is
                            the one row you need to find in the sheet. */}
                        <td className="intake-table-rownum mono" title={row.errors.join('\n')}>
                          {row.errors.length > 0 && <AlertTriangle size={12} />}
                          {row.sourceRow}
                        </td>
                        {table.headers.map((_, ci) => (
                          <td key={ci} className={colMap[ci] ? '' : 'intake-cell--ignored'}>{table.rows[ri]?.[ci] || ''}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {mapped.length > PREVIEW && <p className="intake-preview-more">Showing first {PREVIEW} of {mapped.length} rows.</p>}

              {/* Every bad row, not just the ones inside the preview window. The
                  reasons were previously only in a hover tooltip on the first
                  eight rows, so a problem at row 400 was invisible. */}
              {invalid.length > 0 && (
                <div className="intake-issues">
                  <p className="intake-issues__title">
                    <AlertTriangle size={14} /> {invalid.length} row{invalid.length === 1 ? '' : 's'} need attention
                  </p>
                  <ul className="intake-issues__list">
                    {invalid.slice(0, MAX_ISSUES_SHOWN).map((row) => (
                      <li key={row.sourceRow}>
                        <span className="mono">Row {row.sourceRow}</span> — {row.errors.join('; ')}
                      </li>
                    ))}
                  </ul>
                  {invalid.length > MAX_ISSUES_SHOWN && (
                    <p className="intake-issues__more">…and {invalid.length - MAX_ISSUES_SHOWN} more.</p>
                  )}
                </div>
              )}

              <div className="intake-footer">
                <label className="intake-check">
                  <input type="checkbox" checked={skipInvalid} onChange={(e) => setSkipInvalid(e.target.checked)} disabled={invalid.length === 0} />
                  <span>Skip {invalid.length} row{invalid.length === 1 ? '' : 's'} with issues</span>
                </label>
                <button type="button" className="btn btn-primary btn-sm" disabled={!canImport || mutation.isPending} onClick={() => mutation.mutate()}>
                  <Plus size={14} />{mutation.isPending ? 'Importing…' : `Import ${importable.length} task${importable.length === 1 ? '' : 's'}`}
                </button>
              </div>
              {mutation.isError && (
                <div className="intake-issues intake-issues--error">
                  <p className="intake-issues__title">
                    <AlertTriangle size={14} /> Import rejected
                  </p>
                  {serverIssues.length > 0 ? (
                    <ul className="intake-issues__list">
                      {serverIssues.slice(0, MAX_ISSUES_SHOWN).map((line, i) => (
                        <li key={i}>{line}</li>
                      ))}
                    </ul>
                  ) : (
                    <p>{(mutation.error as Error).message}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
